import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PatientPageHeading from "../../components/organisms/patient-dashboard/PatientPageHeading";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import DocumentUploadField from "../../components/identity-verification/DocumentUploadField";
import { usePatientAuth } from "../../store/authPatientStore";
import { createEnfant } from "../../api/liaisonParentEnfant";

/**
 * Système Parent-Enfant : le parent (patient majeur déjà authentifié) crée un profil enfant
 * rattaché au sien. NIP obligatoire, justificatif de filiation requis (revue asynchrone par un
 * vérificateur — le profil est utilisable immédiatement, cf. spec DAY_3).
 */
export default function AjouterEnfant() {
  const navigate = useNavigate();
  const { refreshEnfants, setActiveProfil } = usePatientAuth();

  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [dateNaissance, setDateNaissance] = useState("");
  const [nip, setNip] = useState("");
  const [justificatifUrl, setJustificatifUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUploadSubmitted = async (url: string) => {
    setJustificatifUrl(url);
    return { ok: true as const };
  };

  const canSubmit =
    nom.trim().length >= 2 &&
    prenom.trim().length >= 2 &&
    !!dateNaissance &&
    /^\d{10}$/.test(nip) &&
    !!justificatifUrl;

  const handleSubmit = async () => {
    if (!canSubmit || !justificatifUrl) return;
    setSubmitting(true);
    setError(null);
    const res = await createEnfant({
      nom: nom.trim(),
      prenom: prenom.trim(),
      dateNaissance,
      nip: nip.trim(),
      justificatifFiliationUrl: justificatifUrl,
    });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    await refreshEnfants();
    setActiveProfil({
      nip: res.data.enfant.nip,
      nom: res.data.enfant.nom,
      prenom: res.data.enfant.prenom,
      isEnfant: true,
    });
    navigate("/patient");
  };

  return (
    <div className="space-y-6">
      <PatientPageHeading
        title="Ajouter un profil enfant"
        description="Créez le profil d'un enfant mineur à votre charge. Vous agirez pour lui/elle sur toutes les opérations (rendez-vous, dossier, ordonnances) jusqu'à sa majorité."
      />

      <Card>
        <CardHeader>
          <CardTitle>Identité de l&apos;enfant</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="enfant-nom" className="block text-sm font-medium text-[#0B1F16] mb-1">
                Nom
              </label>
              <input
                id="enfant-nom"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
              />
            </div>
            <div>
              <label htmlFor="enfant-prenom" className="block text-sm font-medium text-[#0B1F16] mb-1">
                Prénom
              </label>
              <input
                id="enfant-prenom"
                value={prenom}
                onChange={(e) => setPrenom(e.target.value)}
                className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
              />
            </div>
            <div>
              <label htmlFor="enfant-date-naissance" className="block text-sm font-medium text-[#0B1F16] mb-1">
                Date de naissance
              </label>
              <input
                id="enfant-date-naissance"
                type="date"
                value={dateNaissance}
                onChange={(e) => setDateNaissance(e.target.value)}
                className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
              />
            </div>
            <div>
              <label htmlFor="enfant-nip" className="block text-sm font-medium text-[#0B1F16] mb-1">
                NIP de l&apos;enfant (10 chiffres)
              </label>
              <input
                id="enfant-nip"
                value={nip}
                onChange={(e) => setNip(e.target.value.replace(/\D/g, "").slice(0, 10))}
                inputMode="numeric"
                className="w-full rounded-[8px] border border-black/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0CA37F]/40"
              />
            </div>
          </div>

          <DocumentUploadField
            label="Justificatif de filiation (acte de naissance, livret de famille)"
            folderName="liaison-parent-enfant/filiation"
            filename={nip ? `filiation_${nip}` : undefined}
            currentUrl={justificatifUrl}
            onSubmitted={handleUploadSubmitted}
          />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <Button type="button" disabled={!canSubmit || submitting} onClick={() => void handleSubmit()}>
            {submitting ? "Création…" : "Créer le profil enfant"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
